/**
 * Payment mapping recommendation engine.
 *
 * Given a bank credit (narration + amount) and the outstanding invoice book, it
 * recommends the client and invoice the payment most likely settles, and says
 * why. The rules it implements, in the order they are applied:
 *
 *   1. phone number in the narration matched against the client phone list
 *   2. name similarity between the narration and each client name
 *   3. previous payments worded like this one (history)
 *   4. payment vs INVOICE DUE            (always tried first)
 *   5. payment vs INVOICE AMOUNT - TDS   (only when due matching fails)
 *   6. client's overall outstanding      (supporting evidence, never on its own)
 *
 * A client is "identified" when its phone, its name (>= 90%) or its payment
 * history matches. Priority of results:
 *   0  phone number in the narration belongs to the client
 *   1  identified + invoice due match
 *   2  identified + TDS-adjusted invoice match
 *   3  identified, no amount lines up (nearest invoice is reported)
 *   4  not identified, invoice due equals the payment
 *   5  not identified, TDS-adjusted invoice equals the payment
 *   6  none of the above
 *
 * Order within the list: phone first, then any client with matching history,
 * then priority, then name %, then closest amount, then the client with the
 * fewest open invoices, then the most recent invoice.
 *
 * TDS is ALWAYS computed on the invoice amount, never on the due.
 *
 * Speed: the invoice book is indexed once per load — an inverted token index for
 * names and rupee-bucketed maps for due and TDS-adjusted amounts — so scoring a
 * payment touches a handful of candidates instead of all 11k invoices.
 */

export const TDS_RATES = [0.25, 0.5, 1, 1.25, 1.5, 2, 5, 10];
export const STRONG_NAME_MATCH = 90; // percent
// How many clients may share a word before it stops identifying anybody.
export const SIGNATURE_MAX_CLIENTS = 3;
export const DEFAULT_TOLERANCE = 1; // rupees — bank amounts can differ by rounding

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const normalize = (s) =>
  (s ?? '')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const compact = (s) => normalize(s).replace(/ /g, '');

// " - Gopalganj", ", Lucknow", " (Unit 2)" — everything from the first branch
// separator onwards is a location, not the client's identity.
const BRANCH_SUFFIX = /\s*[-–,(/]\s*/;

// Words that say nothing about WHICH client this is: the industry itself, the
// legal suffixes, and the bank/rail vocabulary of a narration.
const STOPWORDS = new Set([
  'and', 'the', 'of', 'for', 'llp', 'pvt', 'ltd', 'private', 'limited', 'co', 'company', 'inc',
  'clinic', 'clinics', 'hospital', 'hospitals', 'diagnostic', 'diagnostics', 'scan', 'scans',
  'centre', 'center', 'xray', 'ray', 'lab', 'labs', 'laboratory', 'imaging', 'digital', 'care',
  'health', 'healthcare', 'medical', 'multispeciality', 'speciality', 'specialty', 'trust',
  'services', 'service', 'solutions', 'institute', 'research', 'unit',
  'medicare', 'medicity', 'city', 'nursing', 'home', 'foundation', 'memorial', 'superspeciality',
  'multispecialty', 'polyclinic', 'radiology', 'sonography', 'mri', 'ct', 'pet',
  // honorifics and titles: on hundreds of names, identifying none of them
  'sri', 'shri', 'sree', 'shree', 'smt', 'messrs', 'saint', 'prof',
  'science', 'sciences', 'medicine', 'medicines', 'institution', 'college', 'university',
  'charitable', 'society', 'welfare', 'group', 'enterprises', 'international', 'national',
  // rails, banks and statement boilerplate
  'neft', 'rtgs', 'imps', 'upi', 'ift', 'clg', 'ach', 'ecs', 'chq', 'cheque', 'trf', 'trfr',
  'p2a', 'p2m', 'attn', 'inb', 'brn', 'txn', 'impstxn', 'bank', 'banking', 'india', 'indian',
  'payment', 'pay', 'paid', 'via', 'invoice', 'inv', 'bill', 'urgent', 'transfer', 'fund',
  'funds', 'online', 'net', 'netbanking', 'ref', 'from', 'axis', 'icici', 'hdfc', 'sbi', 'sbin',
  'canara', 'baroda', 'kotak', 'idfc', 'punb', 'yes', 'federal', 'union', 'uco', 'phonepe',
  'paytm', 'gpay', 'razorpay', 'cred',
]);

const tokensOf = (text) =>
  normalize(text)
    .split(' ')
    .filter((w) => w.length >= 3 && !STOPWORDS.has(w) && !/^\d+$/.test(w));

/**
 * How much of the client's name is present in the narration, 0-100.
 * Handles the two things bank narrations always do: respace the name
 * ("ANI AM SCANS" for "Aniam Scans") and truncate it ("ADITYA DIAGNOSTICS AND HOSPI").
 */
export const nameSimilarity = (clientName, description, index = null) => {
  const dn = normalize(description);
  if (!normalize(clientName) || !dn) return 0;

  const dc = compact(description);
  // A word carried by exactly one client in the whole book is that client's
  // signature. "QRG" or "SIGMA" in a narration names them even when the rest of
  // the books-name ("Health City") never appears in a bank narration.
  const isSignature = (t) => {
    if (!index || t.length < 3) return false;
    const owners = index.byToken.get(t)?.size ?? 0;
    // One client makes it a signature outright. A handful still names a group —
    // "AUROBINDO" across its institute and its hospital — and every one of them
    // should be offered rather than all of them dropped.
    return owners > 0 && owners <= SIGNATURE_MAX_CLIENTS;
  };

  const score = (name) => {
    const cc = compact(name);
    if (cc.length >= 5 && dc.includes(cc)) return 100; // whole name is in there

    const ct = tokensOf(name);
    if (ct.length === 0) return 0;

    let full = 0;
    let partial = 0;
    let signature = false;
    ct.forEach((t) => {
      if (dn.includes(t) || (t.length >= 5 && dc.includes(t))) {
        full += 1;
        if (isSignature(t)) signature = true;
        return;
      }
      // the narration may be cut short: "HOSPI" for "HOSPITAL", "DIAGNO" for "DIAGNOSTICS"
      const stem = t.slice(0, Math.max(4, Math.ceil(t.length * 0.6)));
      if (stem.length >= 4 && (dn.includes(stem) || dc.includes(stem))) partial += 1;
    });

    const base = Math.round(Math.min(100, ((full + 0.6 * partial) / ct.length) * 100));
    // held just under a full-name match, so an exact one still wins the tie
    return signature ? Math.max(base, 95) : base;
  };

  // A branch qualifier is how the books tell two units apart — "Sigma Scan -
  // Gopalganj", "Medanta Super Speciality Hospital, Lucknow". The payer never
  // types it, so scoring the whole name punishes a perfect brand match. Score
  // the name as stored AND with the qualifier dropped, and keep the better one.
  const asStored = score(clientName);
  const core = score(String(clientName ?? '').split(BRANCH_SUFFIX)[0]);
  if (asStored >= core) return asStored;
  // The brand matched but the branch qualifier did not. Still a strong match —
  // just held a hair below a name that matched in full, so that when a brand has
  // two units and the narration names one of them, that one comes first.
  return Math.min(core, 97);
};

/**
 * Phone numbers in a narration.
 *
 * Only whole digit runs are considered — 10 digits, 11 starting 0, or 12
 * starting 91. Taking any 10-digit window would match inside IMPS reference
 * numbers and account numbers (…/9198459859919485000), which is exactly the
 * false positive to avoid here.
 */
export const extractPhones = (description) => {
  const out = new Set();
  const runs = (description ?? '').toString().match(/\d+/g) || [];
  runs.forEach((run) => {
    if (run.length === 10 && /^[6-9]/.test(run)) out.add(run);
    else if (run.length === 11 && run.startsWith('0') && /^0[6-9]/.test(run)) out.add(run.slice(1));
    else if (run.length === 12 && run.startsWith('91') && /^91[6-9]/.test(run)) out.add(run.slice(2));
  });
  return Array.from(out);
};

const phoneKey = (v) => {
  const digits = (v ?? '').toString().replace(/\D/g, '');
  if (digits.length === 10) return digits;
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length > 10) return digits.slice(-10); // stored with a country code or spaces
  return '';
};

/**
 * phone -> clients, from whatever shape the clients-with-phone feed uses.
 * A number shared by several clients is kept as several entries; a shared number
 * cannot identify anybody, so the caller ignores those.
 */
export const buildPhoneIndex = (rows) => {
  const byPhone = new Map();
  (rows || []).forEach((r) => {
    const client = {
      client_id: r.client_id ?? r.client_fk ?? r.id,
      client_name: r.client_name ?? r.name ?? '',
      unique_id: r.unique_id ?? '',
    };
    const numbers = [
      r.phone, r.phone_no, r.phone_number, r.mobile, r.mobile_no, r.contact, r.contact_no,
      ...(Array.isArray(r.phones) ? r.phones : []),
      ...(Array.isArray(r.contacts) ? r.contacts.map((c) => c?.phone ?? c?.mobile ?? c) : []),
    ];
    numbers.forEach((n) => {
      const key = phoneKey(n);
      if (!key) return;
      const list = byPhone.get(key);
      if (list) {
        if (!list.some((c) => String(c.client_id) === String(client.client_id))) list.push(client);
      } else byPhone.set(key, [client]);
    });
  });
  return byPhone;
};

/** Payment expected on an invoice once TDS is withheld. TDS is on the invoice value. */
export const expectedAfterTds = (invoiceAmount, tdsPct) =>
  Math.round(num(invoiceAmount) * (1 - num(tdsPct) / 100));

const MONTH_NUM = {
  JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
  JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12,
};

/** When an invoice was raised, for "most recent month" ordering. 0 if unknown. */
export const invoiceTimestamp = (inv) => {
  const d = inv?.inv_date ? new Date(inv.inv_date) : null;
  if (d && !Number.isNaN(d.getTime())) return d.getTime();
  // no date: read the month off a reference like 5C/JUL-26/R4985
  const m = (inv?.reference_no ?? '')
    .toString()
    .toUpperCase()
    .match(/(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[^0-9]?(\d{2})/);
  if (!m) return 0;
  return Date.UTC(2000 + Number(m[2]), MONTH_NUM[m[1]] - 1, 1);
};

const bucket = (n) => Math.round(num(n));

const pushTo = (map, key, value) => {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
};

/**
 * Index the invoice book once. Everything a payment needs is then a map lookup.
 */
export const buildRecommendationIndex = (invoices, { tolerance = DEFAULT_TOLERANCE } = {}) => {
  const clients = new Map(); // key -> client with its invoices
  const byToken = new Map(); // name token -> Set(client key)
  const compactList = []; // [clientKey, name with no spaces] for the substring pass
  const byDue = new Map(); // rupee bucket -> [{ clientKey, invoice }]
  const byTds = new Map(); // rupee bucket -> [{ clientKey, invoice, tds }]

  (invoices || []).forEach((inv) => {
    const clientKey = String(inv.client_id ?? inv.client_name ?? '');
    if (!clientKey) return;

    let client = clients.get(clientKey);
    if (!client) {
      client = {
        key: clientKey,
        client_id: inv.client_id,
        client_name: inv.client_name ?? '',
        unique_id: inv.unique_id ?? '',
        group_name: inv.group_name ?? '',
        invoices: [],
        totalDue: 0,
      };
      clients.set(clientKey, client);
      compactList.push([clientKey, compact(client.client_name)]);
      tokensOf(client.client_name).forEach((t) => {
        const set = byToken.get(t);
        if (set) set.add(clientKey);
        else byToken.set(t, new Set([clientKey]));
      });
    }

    const due = num(inv.due);
    const invoiceAmount = num(inv.invoice_amount) || due;
    client.invoices.push(inv);
    client.totalDue += due;

    if (due > 0) pushTo(byDue, bucket(due), { clientKey, invoice: inv });
    if (invoiceAmount > 0) {
      TDS_RATES.forEach((tds) => {
        pushTo(byTds, bucket(expectedAfterTds(invoiceAmount, tds)), { clientKey, invoice: inv, tds });
      });
    }
  });

  return { clients, byToken, compactList, byDue, byTds, tolerance };
};

// every entry within +/- tolerance of the amount
const lookupAmount = (map, amount, tolerance) => {
  const hits = [];
  const centre = bucket(amount);
  const span = Math.max(0, Math.round(tolerance));
  for (let d = -span; d <= span; d += 1) {
    const list = map.get(centre + d);
    if (list) hits.push(...list);
  }
  return hits;
};

const amountMatchPct = (expected, actual) => {
  const a = Math.abs(num(actual));
  if (!a) return 0;
  const diff = Math.abs(num(expected) - a);
  return Math.max(0, Math.round((1 - diff / a) * 100));
};

// A repeated, near-identical past narration is trustworthy on its own; a single
// loose one is not. This is the line between "supporting signal" and "reason".
export const RELIABLE_HISTORY = { similarity: 0.9, count: 3 };
export const isReliableHistory = (h) =>
  !!h && h.similarity >= RELIABLE_HISTORY.similarity && (h.count || 1) >= RELIABLE_HISTORY.count;

const confidenceOf = (tier, exact, namePct, { phone = false, history = null } = {}) => {
  const reliableHistory = isReliableHistory(history);
  // an exact phone match is identity, not inference
  if (phone && exact) return 'HIGH';
  if (phone) return 'MEDIUM';
  if (tier <= 2 && exact) return 'HIGH';
  if (tier <= 2) return reliableHistory ? 'HIGH' : 'MEDIUM';
  // no name match: the amount alone is weak, but a repeated history pattern
  // backing the same client makes it solid
  if (exact && reliableHistory) return 'HIGH';
  if (exact && (namePct > 0 || history)) return 'MEDIUM';
  return 'LOW';
};

/**
 * Recommend client + invoice for one payment.
 * Returns every supported candidate, best first, so the finance team can review
 * when nothing is conclusive.
 */
export const recommendForPayment = (
  index,
  payment,
  { tolerance, limit = 8, phoneIndex = null, historyFor = null } = {}
) => {
  if (!index) return [];
  const tol = tolerance ?? index.tolerance ?? DEFAULT_TOLERANCE;
  const description = payment?.description ?? '';
  const amount = num(payment?.amount);
  const historyOf = (client) => (typeof historyFor === 'function' ? historyFor(client) : null);

  // ── a phone number in the narration that belongs to exactly one client is the
  // strongest identity there is: it goes first, ahead of name and amount ──
  const phoneClients = new Map(); // clientKey -> matched number
  if (phoneIndex && phoneIndex.size) {
    extractPhones(description).forEach((n) => {
      const hits = phoneIndex.get(n) || [];
      if (hits.length !== 1) return; // shared or unknown number identifies nobody
      const hit = hits[0];
      const key = String(hit.client_id ?? hit.client_name ?? '');
      if (key && index.clients.has(key)) phoneClients.set(key, n);
    });
  }

  // ── candidate clients by name, via the inverted index (not a full scan) ──
  const namePct = new Map();
  const descTokens = tokensOf(description);
  const seen = new Set();
  descTokens.forEach((t) => {
    (index.byToken.get(t) || []).forEach((k) => seen.add(k));
  });
  // The token index only finds exact word matches, so a client stored as
  // "Sigmascan Diagnostics" is never even considered for a narration reading
  // "SIGMA SCAN". Scan the compacted names too: a distinctive narration word
  // inside a client name, or a whole client name inside the narration.
  const descCompact = compact(description);
  const longTokens = descTokens.filter((t) => t.length >= 3);
  if (longTokens.length || descCompact) {
    index.compactList.forEach(([k, cc]) => {
      if (!cc || seen.has(k)) return;
      if (cc.length >= 5 && descCompact.includes(cc)) {
        seen.add(k);
        return;
      }
      if (longTokens.some((t) => cc.includes(t))) seen.add(k);
    });
  }

  // A narration worded like a payment already mapped to a client names that
  // client outright, even when its books-name looks nothing like the narration.
  // Checked here, before the amount, so history can identify the payer on its
  // own — it still never auto-maps unless the pattern is reliable and repeated.
  if (historyFor) {
    index.clients.forEach((c, k) => {
      if (!seen.has(k) && historyOf(c)) seen.add(k);
    });
  }

  // clients that share any amount hit — a small set, and it is where the truth
  // usually is when the narration carries no name at all
  const dueHits = lookupAmount(index.byDue, amount, tol);
  const tdsHits = lookupAmount(index.byTds, amount, tol);
  dueHits.forEach((h) => seen.add(h.clientKey));
  tdsHits.forEach((h) => seen.add(h.clientKey));
  phoneClients.forEach((_n, k) => seen.add(k));

  seen.forEach((k) => {
    const c = index.clients.get(k);
    if (c) namePct.set(k, nameSimilarity(c.client_name, description, index));
  });

  // which signal names this client, for the label on the card
  const viaOf = (clientKey) => {
    if (phoneClients.get(clientKey)) return 'PHONE';
    if ((namePct.get(clientKey) ?? 0) >= STRONG_NAME_MATCH) return 'NAME';
    const c = index.clients.get(clientKey);
    if (c && historyOf(c)) return 'HISTORY';
    return 'AMOUNT';
  };

  const results = [];
  const add = (clientKey, invoice, tds, expected, method) => {
    const client = index.clients.get(clientKey);
    if (!client) return;
    const pct = namePct.get(clientKey) ?? 0;
    const strongName = pct >= STRONG_NAME_MATCH;
    const viaDue = tds === null;
    const phone = phoneClients.get(clientKey) || null;
    const history = historyOf(client);
    const reliableHistory = isReliableHistory(history);
    // Phone identity outranks everything. Otherwise the payer is "identified" when
    // the narration carries its name OR its wording matches a payment already
    // mapped to it — either one, together with an amount that lines up, is the
    // strongest evidence available and belongs at the top.
    const identified = strongName || !!history;
    const baseTier = identified ? (viaDue ? 1 : 2) : viaDue ? 4 : 5;
    const tier = phone ? 0 : reliableHistory ? Math.max(1, baseTier - 2) : baseTier;
    const diff = Math.abs(expected - amount);
    const exact = diff <= tol;
    const reasons = [];
    if (phone) reasons.push(`phone ${phone} in the narration belongs to this client`);
    if (strongName) reasons.push(`client name matches the narration (${pct}%)`);
    else if (pct > 0) reasons.push(`partial name match (${pct}%)`);
    if (history) {
      reasons.push(
        reliableHistory
          ? `this payer has been mapped here ${history.count} times before with the same wording`
          : `similar narration mapped here before${history.count > 1 ? ` (${history.count}x)` : ''}`
      );
    }
    if (viaDue) {
      reasons.push(
        exact ? `payment equals the invoice due (${expected})` : `payment is within ${Math.round(diff)} of the invoice due`
      );
    } else {
      reasons.push(
        `invoice ${num(invoice.invoice_amount) || num(invoice.due)} less ${tds}% TDS = ${expected}, which matches the payment`
      );
    }
    if (Math.abs(client.totalDue - amount) <= tol) reasons.push("also equals the client's overall outstanding");
    if (client.invoices.length <= 2) {
      reasons.push(`client has only ${client.invoices.length} open invoice${client.invoices.length === 1 ? '' : 's'}`);
    }
    const methodLabel = phone ? `PHONE + ${method.replace(/^(NAME|AMOUNT) \+ /, '')}` : method;

    results.push({
      clientKey,
      client_id: client.client_id,
      client_name: client.client_name,
      unique_id: client.unique_id,
      group_name: client.group_name,
      invoice_num: invoice.reference_no,
      invoice_amount: num(invoice.invoice_amount) || num(invoice.due),
      invoice_due: num(invoice.due),
      invoice_date: invoice.inv_date ?? null,
      invoice_ts: invoiceTimestamp(invoice),
      client_invoice_count: client.invoices.length,
      client_overall_due: Math.round(client.totalDue),
      payment_amount: amount,
      tds_pct: tds,
      expected_amount: expected,
      name_match_pct: pct,
      amount_match_pct: amountMatchPct(expected, amount),
      matching_method: methodLabel,
      tier,
      exact,
      phone_match: phone,
      history_count: history?.count ?? 0,
      history_reliable: reliableHistory,
      confidence: confidenceOf(tier, exact, pct, { phone: !!phone, history }),
      reason: reasons.join('; '),
    });
  };

  dueHits.forEach(({ clientKey, invoice }) => {
    add(clientKey, invoice, null, bucket(invoice.due), `${viaOf(clientKey)} + INVOICE DUE`);
  });

  // TDS matching only fills gaps: skip any invoice already matched on its due
  const dueMatched = new Set(dueHits.map((h) => `${h.clientKey}|${h.invoice.reference_no}`));
  tdsHits.forEach(({ clientKey, invoice, tds }) => {
    if (dueMatched.has(`${clientKey}|${invoice.reference_no}`)) return;
    add(
      clientKey,
      invoice,
      tds,
      expectedAfterTds(num(invoice.invoice_amount) || num(invoice.due), tds),
      `${viaOf(clientKey)} + TDS + INVOICE`
    );
  });

  // Identity without amount support: a phone match, a strong name, or a repeated
  // history pattern. Shown (phone first, as asked) but never HIGH confidence on
  // its own, because no invoice has been pinned down.
  const withAmount = new Set(results.map((r) => r.clientKey));
  const identityOnly = new Set([...namePct.keys(), ...phoneClients.keys()]);
  identityOnly.forEach((k) => {
    if (withAmount.has(k)) return;
    const pct = namePct.get(k) ?? 0;
    const phone = phoneClients.get(k) || null;
    const client = index.clients.get(k);
    if (!client) return;
    const history = historyOf(client);
    if (!phone && pct < STRONG_NAME_MATCH && !history) return;
    const overallExact = Math.abs(client.totalDue - amount) <= tol;
    const reliableHistory = isReliableHistory(history);
    const strongName = pct >= STRONG_NAME_MATCH;
    // The client is identified but no invoice lands on the amount. Point at the
    // one that came closest — a near miss on a named client is worth a look, and
    // it is usually a short payment, a rounding, or a TDS rate we do not list.
    let near = null;
    if (strongName || phone || history) {
      client.invoices.forEach((inv) => {
        const base = num(inv.due) || num(inv.invoice_amount);
        if (!base) return;
        const d = Math.abs(base - amount);
        if (!near || d < near.diff) near = { inv, diff: d, base };
      });
    }
    // never round a near miss up to 100 — the whole point is that it is not exact
    const nearPct = near && amount
      ? near.diff <= tol
        ? 100
        : Math.min(99, Math.max(0, Math.floor(100 - (near.diff / amount) * 100)))
      : 0;
    const nearEnough = !!near && nearPct >= 95;
    const why = [];
    if (phone) why.push(`phone ${phone} in the narration belongs to this client`);
    if (pct >= STRONG_NAME_MATCH) why.push(`client name matches the narration (${pct}%)`);
    if (reliableHistory) why.push(`mapped here ${history.count} times before with the same wording`);
    if (overallExact) {
      why.push("payment equals the client's overall outstanding, but no single invoice matches");
    } else if (near) {
      const gap = Math.round(near.diff);
      const side = amount < near.base ? 'short by' : 'over by';
      const ratio = near.base ? (near.diff / near.base) * 100 : 0;
      why.push(
        `closest invoice ${near.inv.reference_no} is ${Math.round(near.base)} against a payment of ` +
          `${Math.round(amount)} — ${side} ${gap}` +
          // a small shortfall on a named client is usually a deduction, not a different client
          (amount < near.base && ratio >= 0.1 && ratio <= 12
            ? ` (${ratio.toFixed(2)}% of the invoice — check for a TDS rate not in the list)`
            : '')
      );
    } else {
      why.push('no invoice due or TDS amount matches this payment');
    }
    if (!strongName && !phone && history) {
      why.push('found from the wording of a previous payment, not from the name in the narration');
    }
    results.push({
      clientKey: k,
      client_id: client.client_id,
      client_name: client.client_name,
      unique_id: client.unique_id,
      group_name: client.group_name,
      invoice_num: near ? near.inv.reference_no : null,
      invoice_amount: near ? Math.round(num(near.inv.invoice_amount) || near.base) : null,
      invoice_due: near ? Math.round(num(near.inv.due)) : null,
      invoice_date: near ? (near.inv.inv_date ?? null) : null,
      invoice_ts: near ? invoiceTimestamp(near.inv) : 0,
      client_invoice_count: client.invoices.length,
      client_overall_due: Math.round(client.totalDue),
      payment_amount: amount,
      tds_pct: null,
      expected_amount: overallExact ? Math.round(client.totalDue) : near ? Math.round(near.base) : null,
      name_match_pct: pct,
      amount_match_pct: overallExact ? 100 : nearPct,
      // say which signal actually found this client, not which one we hoped for
      matching_method: `${phone ? 'PHONE' : strongName ? 'NAME' : 'HISTORY'} ${
        overallExact ? '+ CLIENT DUE' : near ? '+ NEAREST INVOICE' : 'ONLY'
      }`,
      // a client named by the narration or by its own payment history sits above
      // an unnamed one whose amount merely coincides
      tier: phone ? 0 : strongName || history ? 3 : 6,
      exact: overallExact,
      phone_match: phone,
      history_count: history?.count ?? 0,
      history_reliable: reliableHistory,
      confidence: phone
        ? confidenceOf(0, overallExact, pct, { phone: true, history })
        : (strongName || reliableHistory || (history && history.similarity >= 0.9)) &&
          (overallExact || nearEnough)
          ? 'MEDIUM'
          : 'LOW',
      reason: why.join('; '),
    });
  });

  results.sort(
    (a, b) =>
      // an exact phone match is first, always
      (b.phone_match ? 1 : 0) - (a.phone_match ? 1 : 0) ||
      // then anyone this wording has been mapped to before: a payer the books
      // have already seen behaving this way leads the list
      (b.history_count > 0 ? 1 : 0) - (a.history_count > 0 ? 1 : 0) ||
      a.tier - b.tier ||
      b.name_match_pct - a.name_match_pct ||
      Math.abs(a.expected_amount - amount) - Math.abs(b.expected_amount - amount) ||
      // Same amount evidence on several clients: the one with the smallest open
      // book is the likelier owner of this payment — a single matching invoice
      // means far more than one match out of thirty-three.
      a.client_invoice_count - b.client_invoice_count ||
      // then whichever invoice is from the most recent month
      b.invoice_ts - a.invoice_ts ||
      b.invoice_due - a.invoice_due
  );

  return results.slice(0, limit);
};

/** One line per client, best invoice first — what the suggestions band shows. */
export const recommendClients = (index, payment, options) => {
  const all = recommendForPayment(index, payment, { ...options, limit: 100 });
  const byClient = new Map();
  all.forEach((r) => {
    if (!byClient.has(r.clientKey)) byClient.set(r.clientKey, { ...r, alternatives: [] });
    else byClient.get(r.clientKey).alternatives.push(r);
  });
  return Array.from(byClient.values()).slice(0, options?.limit ?? 8);
};
